import { useState, useEffect } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Typography,
} from '@mui/material';
import InsightsIcon from '@mui/icons-material/Insights';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import seoInsightsService, { ProjectInsights, ChangeTypeInsight, ChangeImpact } from '@/services/seoInsights';

type Props = {
  projectId: string;
};

const positionColor = (delta: number | null): string => {
  if (delta == null) return '#96979b';
  if (delta > 0) return '#0D9D58';
  if (delta < 0) return '#EA4335';
  return '#96979b';
};

const changeTypeIcons: Record<string, string> = {
  title_tag: '📝',
  meta_description: '📋',
  content: '📄',
  slug: '🔗',
};

const WhatWorkedInsights = function ({ projectId }: Props): JSX.Element {
  const [insights, setInsights] = useState<ProjectInsights | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!projectId) return;
    seoInsightsService.getProjectInsights(projectId)
      .then(setInsights)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [projectId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 3, justifyContent: 'center' }}>
        <CircularProgress size={16} sx={{ color: '#054AD9' }} />
        <Typography variant="caption" color="text.secondary">Loading insights...</Typography>
      </Box>
    );
  }

  if (!insights || insights.totalChanges === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 3 }}>
        <Typography variant="body2" color="text.secondary">
          No WordPress changes applied yet. Apply AI suggestions to start tracking impact.
        </Typography>
      </Box>
    );
  }

  const measured = insights.byChangeType.filter((ct) => ct.measuredChanges > 0);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <InsightsIcon sx={{ fontSize: 20, color: '#054AD9' }} />
        <Typography variant="subtitle2" fontWeight={700}>What Worked</Typography>
        <Chip
          label={`${insights.totalChanges} changes applied`}
          size="small"
          sx={{ fontSize: 10, height: 20 }}
        />
      </Box>

      {/* Overall stats */}
      {insights.avgPositionImprovement != null && (
        <Box sx={{ p: 2, backgroundColor: insights.avgPositionImprovement > 0 ? '#E6F4EA' : '#FCE8E6', borderRadius: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {insights.avgPositionImprovement > 0 ? (
              <ArrowUpwardIcon sx={{ fontSize: 18, color: '#0D9D58' }} />
            ) : (
              <ArrowDownwardIcon sx={{ fontSize: 18, color: '#EA4335' }} />
            )}
            <Typography variant="h6" fontWeight={700} color={positionColor(insights.avgPositionImprovement)}>
              {insights.avgPositionImprovement > 0 ? '+' : ''}{insights.avgPositionImprovement.toFixed(1)} positions
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            Average impact across {insights.measuredChanges} measured change{insights.measuredChanges !== 1 ? 's' : ''}
          </Typography>
        </Box>
      )}

      {/* By change type — ranked by impact */}
      {measured.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mb: 1, display: 'block', fontSize: 11 }}>
            Impact by Change Type (ranked by effectiveness)
          </Typography>
          {measured.map((ct) => (
            <Box
              key={ct.changeType}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1.5,
                mb: 0.75,
                borderRadius: 2,
                backgroundColor: '#fafafa',
                borderLeft: '3px solid',
                borderColor: positionColor(ct.avgPositionDelta),
              }}
            >
              <Typography sx={{ fontSize: 16 }}>
                {changeTypeIcons[ct.changeType] ?? '📌'}
              </Typography>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" fontWeight={600} sx={{ fontSize: 12 }}>
                  {ct.label}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                  {ct.measuredChanges} measured · {ct.totalChanges} total
                </Typography>
              </Box>
              {ct.avgPositionDelta != null && (
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="body2" fontWeight={700} color={positionColor(ct.avgPositionDelta)} sx={{ fontSize: 13 }}>
                    {ct.avgPositionDelta > 0 ? '+' : ''}{ct.avgPositionDelta.toFixed(1)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: 9 }}>
                    avg. positions
                  </Typography>
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}

      {/* Top performers */}
      {insights.topPerformers.length > 0 && (
        <Box>
          <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mb: 1, display: 'block', fontSize: 11 }}>
            Top Performing Changes
          </Typography>
          {insights.topPerformers.map((c) => (
            <Box key={c.changeId} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, p: 1, borderRadius: 1, backgroundColor: '#f8f9fa' }}>
              <Chip
                label={c.positionDelta != null && c.positionDelta > 0 ? `+${c.positionDelta.toFixed(1)}` : c.positionDelta?.toFixed(1) ?? '?'}
                size="small"
                color={c.positionDelta != null && c.positionDelta > 0 ? 'success' : 'default'}
                sx={{ fontSize: 10, height: 18, fontWeight: 700 }}
              />
              <Box sx={{ flex: 1, overflow: 'hidden' }}>
                <Typography variant="caption" sx={{ fontSize: 10, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {c.newValue.slice(0, 60)}{c.newValue.length > 60 ? '...' : ''}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 9 }}>
                  {c.keyword ?? c.pageUrl ?? 'Unknown'} · {new Date(c.appliedAt).toLocaleDateString()}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* Pending measurements */}
      {insights.totalChanges > insights.measuredChanges && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, fontSize: 10, textAlign: 'center' }}>
          {insights.totalChanges - insights.measuredChanges} change{insights.totalChanges - insights.measuredChanges !== 1 ? 's' : ''} still measuring (takes 7-14 days to see impact)
        </Typography>
      )}
    </Box>
  );
};

export default WhatWorkedInsights;
